import type {
  InviteWorkspaceMembersResponse,
  WorkspaceMembersResponse,
} from '../../lib/workspaces.js';

export type MemberRole = 'Admin' | 'Member';
export type MemberFilter = 'All' | MemberRole;
export type MemberStatus = 'Active' | 'Pending' | 'Expired';
export type InviteDeliveryStatus = 'sent' | 'not_sent';

export interface WorkspaceMember {
  id: string;
  name: string;
  email: string;
  role: MemberRole;
  status: MemberStatus;
  lastActive: string;
  avatarUrl?: string | null;
  sourceType?: 'member' | 'invitation';
  deliveryStatus?: InviteDeliveryStatus;
}

export function mapWorkspaceMembersResponse(response: WorkspaceMembersResponse): {
  canManageMembers: boolean;
  seats: { used: number; limit: number; isOver: boolean; quantity: number; canManage: boolean };
  members: WorkspaceMember[];
} {
  return {
    canManageMembers: response.canManageMembers,
    seats: response.seats,
    members: [
      ...response.members.map(
        (member): WorkspaceMember => ({
          id: member.id,
          name: member.name,
          email: member.email,
          role: member.role === 'owner' || member.role === 'admin' ? 'Admin' : 'Member',
          status: 'Active',
          lastActive: member.lastActive,
          avatarUrl: member.avatarUrl,
          sourceType: 'member',
        }),
      ),
      ...response.invitations
        .filter((invite) => invite.status !== 'Revoked')
        .map(
          (invite): WorkspaceMember => ({
            id: invite.id,
            name: invite.name,
            email: invite.email,
            role: invite.role === 'admin' ? 'Admin' : 'Member',
            status: invite.status === 'Expired' ? 'Expired' : 'Pending',
            deliveryStatus: invite.deliveryStatus,
            lastActive: invite.lastActive,
            avatarUrl: null,
            sourceType: 'invitation',
          }),
        ),
    ],
  };
}

/**
 * Two-letter fallback for a member with no avatar image.
 *
 * Lives here rather than next to the one table that first needed it: the Spec
 * AI seat picker draws the same avatar cell, and two copies of this rule is how
 * the two lists start rendering the same person differently.
 */
export function getMemberInitials(member: Pick<WorkspaceMember, 'name' | 'email'>): string {
  const source = member.name || member.email.replace(/@.*/, '');
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  const [first = 'U', second] = parts;

  return (second ? `${first.charAt(0)}${second.charAt(0)}` : first.slice(0, 2)).toUpperCase();
}

export function getMemberStatusLabel(member: WorkspaceMember): MemberStatus | 'Retry needed' {
  if (member.sourceType === 'invitation' && member.deliveryStatus === 'not_sent') {
    return 'Retry needed';
  }

  return member.status;
}

export function getInviteErrorMessages(response: InviteWorkspaceMembersResponse): string[] {
  const messages = new Map<string, string>();

  for (const email of response.invalid) {
    const key = `${email}:invalid_invite_email:Enter a valid email address.`;
    messages.set(key, `${email}: Enter a valid email address.`);
  }

  for (const error of response.errors ?? []) {
    const email = error.email ?? '';
    const label = email ? `${email}: ${error.message}` : error.message;
    const key = `${email}:${error.code}:${error.message}`;
    messages.set(key, label);
  }

  return Array.from(messages.values());
}

export function getInviteDomainWarnings(input: string, approvedDomains: string[]): string[] {
  const approved = new Set(approvedDomains.map((domain) => domain.trim().toLowerCase()));
  if (approved.size === 0) return [];

  const warnedDomains = new Set<string>();
  const warnings: string[] = [];

  for (const value of input.split(',')) {
    const email = value.trim().toLowerCase();
    const atIndex = email.lastIndexOf('@');
    const domain = atIndex >= 0 ? email.slice(atIndex + 1) : '';
    if (!email || !domain || email.includes(' ') || !email.includes('.') || !domain.includes('.')) {
      continue;
    }

    if (!approved.has(domain) && !warnedDomains.has(domain)) {
      warnedDomains.add(domain);
      warnings.push(`${domain} is not an approved email domain. Direct invite will still be sent.`);
    }
  }

  return warnings;
}

export function mapInviteResponseToPendingMembers(
  response: InviteWorkspaceMembersResponse,
  role: MemberRole,
): WorkspaceMember[] {
  return response.invitations.map((invitation) => ({
    id: invitation.id,
    name: invitation.email.split('@')[0] || invitation.email,
    email: invitation.email,
    role,
    status: 'Pending',
    lastActive: 'Pending',
    avatarUrl: null,
    sourceType: 'invitation',
  }));
}

export function mergeInviteResponsePendingMembers(
  currentMembers: WorkspaceMember[],
  response: InviteWorkspaceMembersResponse,
  role: MemberRole,
): WorkspaceMember[] {
  const existingIds = new Set(currentMembers.map((member) => member.id));
  const existingEmails = new Set(currentMembers.map((member) => member.email.toLowerCase()));
  const pendingMembers = mapInviteResponseToPendingMembers(response, role).filter((member) => {
    const email = member.email.toLowerCase();
    if (existingIds.has(member.id) || existingEmails.has(email)) return false;
    existingIds.add(member.id);
    existingEmails.add(email);
    return true;
  });

  return [...pendingMembers, ...currentMembers];
}

export function removeInvitationMember(
  currentMembers: WorkspaceMember[],
  invitationId: string,
): WorkspaceMember[] {
  return currentMembers.filter(
    (member) => member.id !== invitationId || member.sourceType !== 'invitation',
  );
}

export function filterMembers(
  members: WorkspaceMember[],
  query: string,
  roleFilter: MemberFilter,
): WorkspaceMember[] {
  const normalizedQuery = query.trim().toLowerCase();

  return members.filter((member) => {
    const matchesQuery =
      !normalizedQuery ||
      member.name.toLowerCase().includes(normalizedQuery) ||
      member.email.toLowerCase().includes(normalizedQuery);
    const matchesRole = roleFilter === 'All' || member.role === roleFilter;

    return matchesQuery && matchesRole;
  });
}

export function getActiveMemberCount(members: WorkspaceMember[]): number {
  return members.filter((member) => member.status === 'Active').length;
}

export function getInviteCount(members: WorkspaceMember[]): number {
  return members.filter((member) => member.status === 'Pending' || member.status === 'Expired')
    .length;
}
